import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button } from 'reactstrap';
import { PlusCircle, RefreshCw } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import CustomizerContext from '../../../../_helper/Customizer';
import { fetchLoadingWheel, fetchAddWheel, fetchEditWheel, fetchDeleteWheel, fetchToggleStatusWheel } from '../../../../api/wheel';
import { toast } from 'react-toastify';

// Import components
import StatisticsCards from './StatisticsCards';
import MinigameCard from './MinigameCard';
import MinigameFormModal from './MinigameFormModal';
import MinigameDetailModal from './MinigameDetailModal';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';

const WheelMinigameManagement = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const [minigames, setMinigames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMinigame, setCurrentMinigame] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    reward: '',
    status: 'active'
  });

  // Load minigames khi component mount
  useEffect(() => {
    loadMinigames();
  }, []);

  // Hàm load danh sách minigame
  const loadMinigames = async () => {
    setLoading(true);
    try {
      const data = await fetchLoadingWheel();
      setMinigames(data);
      toast.success('✅ Tải danh sách minigame thành công!');
    } catch (error) {
      toast.error('❌ Lỗi khi tải minigame: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      reward: '',
      status: 'active'
    });
  };

  // Mở modal thêm mới
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentMinigame(null);
    resetForm();
    setModalOpen(true);
  };

  // Mở modal chỉnh sửa
  const handleEdit = (minigame) => {
    setEditMode(true);
    setCurrentMinigame(minigame);
    setFormData({
      title: minigame.title || '',
      description: minigame.description || '',
      startDate: minigame.startDate ? new Date(minigame.startDate).toISOString().slice(0, 16) : '',
      endDate: minigame.endDate ? new Date(minigame.endDate).toISOString().slice(0, 16) : '',
      reward: minigame.reward || '',
      status: minigame.status || 'active'
    });
    setModalOpen(true);
  };

  // Xem chi tiết
  const handleViewDetail = (minigame) => {
    setCurrentMinigame(minigame);
    setDetailModalOpen(true);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.title || formData.title.trim().length < 3) {
      toast.warning('⚠️ Tên minigame phải có ít nhất 3 ký tự!');
      return false;
    }
    if (!formData.description || formData.description.trim().length < 10) {
      toast.warning('⚠️ Mô tả phải có ít nhất 10 ký tự!');
      return false;
    }
    if (!formData.startDate || !formData.endDate) {
      toast.warning('⚠️ Vui lòng chọn ngày bắt đầu và kết thúc!');
      return false;
    }
    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      toast.warning('⚠️ Ngày kết thúc phải sau ngày bắt đầu!');
      return false;
    }
    if (!formData.reward || Number(formData.reward) < 0) {
      toast.warning('⚠️ Phần thưởng phải >= 0!');
      return false;
    }
    return true;
  };

  // Xử lý submit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const minigameData = {
        title: formData.title,
        description: formData.description,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        reward: Number(formData.reward),
        status: formData.status
      };

      if (editMode && currentMinigame) {
        await fetchEditWheel(currentMinigame._id, minigameData);
        toast.success('✅ Cập nhật minigame thành công!');
      } else {
        await fetchAddWheel(minigameData);
        toast.success('✅ Thêm minigame mới thành công!');
      }
      
      setModalOpen(false);
      resetForm();
      loadMinigames();
    } catch (error) {
      toast.error('❌ Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa
  const handleDelete = async (id) => {
    if (window.confirm('⚠️ Bạn có chắc chắn muốn xóa minigame này?')) {
      setLoading(true);
      try {
        await fetchDeleteWheel(id);
        toast.success('✅ Xóa minigame thành công!');
        loadMinigames();
      } catch (error) {
        toast.error('❌ Lỗi khi xóa: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Xử lý toggle status
  const handleToggleStatus = async (id, currentStatus) => {
    setLoading(true);
    try {
      await fetchToggleStatusWheel(id);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      toast.success(`✅ ${newStatus === 'active' ? 'Kích hoạt' : 'Vô hiệu hóa'} minigame thành công!`);
      loadMinigames();
    } catch (error) {
      toast.error('❌ Lỗi khi thay đổi trạng thái: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Wheel Minigame" title="Wheel Minigame" mainTitle="Quản lý Wheel Minigame" />
      <Container fluid={true}>
        <Row>
          <Col md="12">
            {/* Info Banner */}
            <Card className="bg-gradient-success text-white mb-4 border-0 shadow">
              <CardBody className="p-4">
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-25 rounded-circle p-3 me-3">
                    <RefreshCw size={32} className="spin-animation" />
                  </div>
                  <div>
                    <h4 className="text-white mb-1">Wheel Minigame Management</h4>
                    <p className="mb-0 opacity-75">
                      Quản lý các minigame vòng quay may mắn
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Statistics Cards */}
            {!loading && minigames.length > 0 && (
              <StatisticsCards minigames={minigames} />
            )}

            {/* Main Content Card */}
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                  <div>
                    <h4 className="mb-1">
                      <RefreshCw size={24} className="me-2 text-success" />
                      Danh sách Minigame
                    </h4>
                    <p className="text-muted mb-0 small">
                      Quản lý các vòng quay may mắn
                    </p>
                  </div>
                  <Button color="success" onClick={handleAddNew} className="shadow-sm">
                    <PlusCircle size={16} className="me-2" />
                    Tạo Minigame Mới
                  </Button>
                </div>

                {/* Loading State */}
                {loading && <LoadingState />}

                {/* Empty State */}
                {!loading && minigames.length === 0 && (
                  <EmptyState onAddNew={handleAddNew} />
                )}

                {/* Minigames Grid */}
                {!loading && minigames.length > 0 && (
                  <Row>
                    {minigames.map((minigame, index) => (
                      <Col xl="4" lg="6" md="6" sm="12" key={minigame._id || index} className="mb-4">
                        <MinigameCard
                          minigame={minigame}
                          onViewDetail={handleViewDetail}
                          onToggleStatus={handleToggleStatus}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Create/Edit */}
        <MinigameFormModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          editMode={editMode}
          formData={formData}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          loading={loading}
        />

        {/* Modal Detail */}
        <MinigameDetailModal
          isOpen={detailModalOpen}
          toggle={() => setDetailModalOpen(false)}
          minigame={currentMinigame}
        />
      </Container>

      {/* Custom CSS */}
      <style>{`
        .hover-lift {
          transition: all 0.3s ease;
        }
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.175) !important;
        }
        .bg-gradient-success {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
        }
        .spin-animation {
          animation: spin 3s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </Fragment>
  );
};

export default WheelMinigameManagement;
